import { BaseService } from '../base/BaseService';
import type { QueryOptions, ServiceResponse } from '../base/ServiceTypes';

import {
  type PersistedConnectionPlatform,
  SOCIAL_ANALYTICS_SYNC_PLATFORM_IDS,
  type SocialAnalyticsSyncPlatform,
  isPersistedConnectionPlatform,
  isSocialAnalyticsSyncPlatform,
} from '@/lib/platforms/connection-capabilities';

/** @deprecated Prefer SocialAnalyticsSyncPlatform in new command callers. */
export type PlatformType = SocialAnalyticsSyncPlatform;

export interface SyncResult {
  platform: PlatformType;
  accountId: string;
  status: 'success' | 'error' | 'skipped';
  success: boolean;
  message?: string;
  postsUpdated?: number;
  metricsUpdated?: number;
  lastSyncedAt?: string;
  error?: string;
  attempted?: boolean;
  reason?: string;
  invokedFunction?: string;
  durationMs?: number;
}

export interface SyncStatus {
  platform: PersistedConnectionPlatform;
  lastSyncedAt: string | null;
  nextSyncAt: string | null;
  status: 'idle' | 'syncing' | 'error';
  errorMessage?: string;
}

export class SocialDataSyncService extends BaseService {
  private static instance: SocialDataSyncService;
  private syncingPlatforms = new Set<SocialAnalyticsSyncPlatform>();

  private constructor() {
    super();
  }

  static getInstance(): SocialDataSyncService {
    if (!SocialDataSyncService.instance) {
      SocialDataSyncService.instance = new SocialDataSyncService();
    }
    return SocialDataSyncService.instance;
  }

  /**
   * Sync data for specific platforms
   */
  async syncPlatforms(
    platforms: SocialAnalyticsSyncPlatform[],
    _options?: QueryOptions,
  ): Promise<ServiceResponse<SyncResult[]>> {
    return this.execute(async () => {
      // Check if any platforms are already syncing
      const alreadySyncing = platforms.filter((p) => this.syncingPlatforms.has(p));
      if (alreadySyncing.length > 0) {
        throw new Error(`Already syncing: ${alreadySyncing.join(', ')}`);
      }

      // Mark platforms as syncing
      platforms.forEach((p) => this.syncingPlatforms.add(p));

      try {
        const { data, error } = await this.client.functions.invoke<unknown>(
          'sync-social-analytics',
          {
            body: { platforms },
          },
        );

        if (error) throw error;
        const syncResults = this.normalizeSyncResults(data);
        if (!syncResults) {
          throw new Error('No sync results returned');
        }

        // Clear cache for synced platforms
        platforms.forEach((platform) => {
          this.clearPlatformCache(platform);
        });

        return syncResults;
      } finally {
        // Remove platforms from syncing set
        platforms.forEach((p) => this.syncingPlatforms.delete(p));
      }
    });
  }

  /**
   * Sync all connected platforms
   */
  async syncAllPlatforms(options?: QueryOptions): Promise<ServiceResponse<SyncResult[]>> {
    return this.syncPlatforms([...SOCIAL_ANALYTICS_SYNC_PLATFORM_IDS], options);
  }

  /**
   * Get sync status for all platforms
   */
  async getSyncStatus(options?: QueryOptions): Promise<ServiceResponse<SyncStatus[]>> {
    return this.execute(async () => {
      const cacheKey = 'sync-status-all';

      return this.executeWithCache(
        cacheKey,
        async () => {
          const { data, error } = await this.client.functions.invoke<unknown>(
            'sync-social-analytics',
            {
              body: { action: 'get_status' },
            },
          );

          if (error) throw error;
          const statuses = this.normalizeSyncStatuses(data);
          if (!statuses) throw new Error('No sync status returned');

          // Add current syncing status
          return statuses.map((status) => ({
            ...status,
            status:
              isSocialAnalyticsSyncPlatform(status.platform) &&
              this.syncingPlatforms.has(status.platform)
                ? 'syncing'
                : status.status,
          }));
        },
        { ttl: 30 * 1000, ...options }, // 30 second cache
      );
    });
  }

  /**
   * Check if a platform is currently syncing
   */
  isSyncing(platform: SocialAnalyticsSyncPlatform): boolean {
    return this.syncingPlatforms.has(platform);
  }

  /**
   * Check if any platform is syncing
   */
  isAnySyncing(): boolean {
    return this.syncingPlatforms.size > 0;
  }

  /**
   * Clear cache for a specific platform
   */
  private clearPlatformCache(platform: SocialAnalyticsSyncPlatform): void {
    // Clear any platform-specific caches using BaseService method
    this.invalidateCacheByPattern(platform);
  }

  /**
   * Clear all sync-related caches
   */
  clearSyncCache(): void {
    this.clearCache('sync-status-all');
    // Clear any other sync-related caches using BaseService method
    this.invalidateCacheByPattern('sync-');
  }

  private normalizeSyncResults(response: unknown): SyncResult[] | null {
    const envelope = this.toObject(response);

    const candidates: unknown[] = [];

    if (Array.isArray(response)) {
      candidates.push(...response);
    } else if (envelope?.syncResults && Array.isArray(envelope.syncResults)) {
      candidates.push(...envelope.syncResults);
    } else if (Array.isArray(envelope?.details?.syncResults)) {
      candidates.push(...envelope.details!.syncResults);
    } else if (Array.isArray((envelope as any)?.details?.results)) {
      candidates.push(...(envelope as any).details.results);
    }

    if (candidates.length === 0) {
      if (response && typeof response === 'object' && envelope?.success === false) {
        return [];
      }
      return null;
    }

    const parsed = candidates
      .map((row) => this.normalizeSingleSyncResult(row))
      .filter((row): row is SyncResult => row !== null);
    return parsed.length > 0 ? parsed : [];
  }

  private normalizeSingleSyncResult(raw: unknown): SyncResult | null {
    if (!raw || typeof raw !== 'object') return null;
    const row = raw as Record<string, unknown>;
    const platform = this.normalizePlatform(row.platform);
    if (!platform) return null;

    const rawStatus = this.normalizeStatus(row.status);
    const status =
      rawStatus ?? (row.success === false ? 'error' : row.success === true ? 'success' : 'skipped');
    const attemptRaw = typeof row.attempted === 'boolean' ? row.attempted : undefined;
    const accountId = this.normalizeString(row.accountId) ?? 'unknown-account';

    return {
      platform,
      accountId,
      status,
      success:
        status === 'success' ||
        (typeof row.success === 'boolean' ? row.success : status === 'success'),
      message: this.normalizeString(row.message),
      postsUpdated: this.normalizeNumber(row.postsUpdated),
      metricsUpdated: this.normalizeNumber(row.metricsUpdated),
      lastSyncedAt: this.normalizeStringOrNull(row.lastSyncedAt),
      error: this.normalizeString(row.error),
      attempted: attemptRaw,
      reason: this.normalizeString(row.reason),
      invokedFunction: this.normalizeString(row.invokedFunction),
      durationMs: this.normalizeNumber(row.durationMs),
    };
  }

  private normalizeStatus(raw: unknown): SyncResult['status'] | null {
    if (typeof raw !== 'string') return null;
    const normalized = raw.toLowerCase();
    if (normalized === 'success' || normalized === 'error' || normalized === 'skipped') {
      return normalized as SyncResult['status'];
    }
    return null;
  }

  private normalizePlatform(platform: unknown): SocialAnalyticsSyncPlatform | null {
    return isSocialAnalyticsSyncPlatform(platform) ? platform : null;
  }

  private normalizeSyncStatuses(response: unknown): SyncStatus[] | null {
    if (!Array.isArray(response)) return null;

    return response
      .map((raw) => this.normalizeSingleSyncStatus(raw))
      .filter((status): status is SyncStatus => status !== null);
  }

  private normalizeSingleSyncStatus(raw: unknown): SyncStatus | null {
    if (!raw || typeof raw !== 'object') return null;
    const row = raw as Record<string, unknown>;
    if (!isPersistedConnectionPlatform(row.platform)) return null;

    const status = row.status;
    return {
      platform: row.platform,
      lastSyncedAt: this.normalizeString(row.lastSyncedAt) ?? null,
      nextSyncAt: this.normalizeString(row.nextSyncAt) ?? null,
      status: status === 'syncing' || status === 'error' ? status : 'idle',
      errorMessage: this.normalizeString(row.errorMessage) ?? this.normalizeString(row.reason),
    };
  }

  private normalizeString(value: unknown): string | undefined {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : undefined;
    }
    return undefined;
  }

  private normalizeStringOrNull(value: unknown): string | undefined {
    return this.normalizeString(value);
  }

  private normalizeNumber(value: unknown): number | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
  }

  private toObject(value: unknown): Record<string, unknown> | null {
    if (!value || typeof value !== 'object') return null;
    return value as Record<string, unknown>;
  }
}

// Export singleton instance
export const socialDataSyncService = SocialDataSyncService.getInstance();
